import styled, { css, keyframes } from 'styled-components'
import colors from 'utils/styling/colors'
import zLayers from 'utils/styling/zLayers'
import {
  PinnableTooltip,
  PinnableTooltipAnchor,
} from '@/components/global/PinnableTooltip/PinnableTooltip.styled'

/** Side length of one photo square in the grid. */
export const TILE_SIZE_PX = 148

/** The case images grid: photo squares that wrap to the available width. */
export const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
`

/**
 * Hover controls on a tile fade in on hover and on keyboard focus inside the
 * tile, so the ellipses button is reachable without a mouse.
 */
export const TileActions = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  opacity: 0;
  transition: opacity 0.15s;
  z-index: ${zLayers.controls};
`

type TileProps = {
  $menuOpen?: boolean
}

/** One photo square. The photo fills it; badges and controls float on top. */
export const Tile = styled.div<TileProps>`
  position: relative;
  width: ${TILE_SIZE_PX}px;
  height: ${TILE_SIZE_PX}px;
  border: 1px solid ${colors('gray4')};
  border-radius: 4px;
  overflow: visible;
  background: ${colors('gray5')};
  transition: box-shadow 0.15s;

  &:hover,
  &:focus-within {
    box-shadow: 0 0 0 2px ${colors('brand', 40)};
  }

  &:hover ${TileActions}, &:focus-within ${TileActions} {
    opacity: 1;
  }

  ${({ $menuOpen }) =>
    $menuOpen &&
    css`
      z-index: ${zLayers.menu};
      ${TileActions} {
        opacity: 1;
      }
    `}
`

/** Clips the photo to the rounded square. */
export const TileImageFrame = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 4px;
  overflow: hidden;
`

const pulse = keyframes`
  from { opacity: 1; }
  to { opacity: 0.5; }
`

type TileImageProps = {
  $pulsing?: boolean
}

/**
 * The photo itself. While an enhancement runs it eases between 50% and
 * 100% opacity; people who asked for reduced motion get a steady 75%.
 */
export const TileImage = styled.img<TileImageProps>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;

  ${({ $pulsing }) =>
    $pulsing &&
    css`
      animation: ${pulse} 1s ease-in-out infinite alternate;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
        opacity: 0.75;
      }
    `}
`

/** Round control button that sits on a photo (ellipses, warning). */
export const TileIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: ${colors('white')};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  cursor: pointer;

  &:hover {
    background: ${colors('gray5')};
  }

  &:focus-visible {
    outline: 2px solid ${colors('brand')};
    outline-offset: 1px;
  }
`

/** The ellipses dropdown, anchored below the button on the tile. */
export const TileMenu = styled.div`
  position: absolute;
  top: 44px;
  /* Anchored to the tile's left edge: a right-anchored menu is wider than
     the tile and gets clipped by the panel edge in the editor's sidebar. */
  left: 8px;
  min-width: 190px;
  padding: 4px;
  background: ${colors('white')};
  border: 1px solid ${colors('gray4')};
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  z-index: ${zLayers.menu};
`

/** One row of the tile dropdown. */
export const TileMenuItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 2px;
  background: transparent;
  color: ${colors('gray1')};
  font-size: 14px;
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: ${colors('gray5')};
    outline: none;
  }

  &:disabled {
    color: ${colors('gray3')};
    cursor: default;
    background: transparent;
  }
`

type DefaultCheckProps = {
  $nudged?: boolean
}

/**
 * The "default image" checkmark, bottom-right. When the "Edited with AI"
 * tag is present it moves up so the two never overlap.
 */
export const DefaultCheck = styled.div<DefaultCheckProps>`
  position: absolute;
  right: 8px;
  bottom: ${({ $nudged }) => ($nudged ? '38px' : '8px')};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${colors('brand')};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transition: bottom 0.15s;
  z-index: ${zLayers.contentBase};
`

/** The "Edited with AI" tag on an accepted enhancement, bottom-left. */
export const AiTag = styled.div`
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 3px;
  background: ${colors('gray1', 80)};
  color: ${colors('white')};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
  z-index: ${zLayers.contentBase};
`

/**
 * The low-resolution tooltip, below its warning icon. It opens and stays
 * open the way every `PinnableTooltip` does.
 */
export const LowResTooltip = styled(PinnableTooltip)`
  top: 28px;
  left: 0;
  width: 220px;
  padding: 10px 12px;
  border-radius: 4px;
  background: ${colors('gray1')};
  color: ${colors('white')};
  font-size: 12px;
  line-height: 1.4;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: ${zLayers.menu};
`

/** Anchor for the low-resolution warning, top-left of the photo. */
export const LowResAnchor = styled(PinnableTooltipAnchor)`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: ${zLayers.controls};
`

/** Small link-style button inside the low-resolution tooltip. */
export const LowResTooltipButton = styled.button`
  margin-top: 8px;
  padding: 6px 10px;
  border: 0;
  border-radius: 3px;
  background: ${colors('brand')};
  color: ${colors('white')};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    filter: brightness(1.1);
  }
`
